import { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from './firebaseClient';
import { QRCodeCanvas } from 'qrcode.react';
import * as XLSX from 'xlsx';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND = 'https://3amenu.com/api';
const SITE_URL = 'https://3amenu.com';

const TEMA_RENKLER = [
  { ad: 'Turuncu', renk: '#f97316' }, { ad: 'Kırmızı', renk: '#ef4444' },
  { ad: 'Mavi', renk: '#3b82f6' }, { ad: 'Yeşil', renk: '#22c55e' },
  { ad: 'Mor', renk: '#a855f7' }, { ad: 'Pembe', renk: '#ec4899' },
  { ad: 'Turkuaz', renk: '#14b8a6' }, { ad: 'Koyu', renk: '#374151' },
];

async function dosyaYukle(dosya, klasor) {
  const formData = new FormData();
  formData.append('dosya', dosya);
  formData.append('klasor', klasor);
  const res = await fetch(`${BACKEND}/menu/upload-resim`, { method: 'POST', body: formData });
  const data = await res.json();
  if (!data.success) throw new Error(data.hata || 'Yükleme başarısız');
  return data.url;
}

async function videoYukle(dosya) {
  const formData = new FormData();
  formData.append('dosya', dosya);
  const res = await fetch(`${BACKEND}/menu/upload-video`, { method: 'POST', body: formData });
  const data = await res.json();
  if (!data.success) throw new Error(data.hata || 'Yükleme başarısız');
  return data.url;
}

// ─────────────────────────────────────────────
// Sürüklenebilir KATEGORİ kartı
// ─────────────────────────────────────────────
function SortableKategoriKart({ kat, altKatlar, onDuzenle, onSil, onAltKatSil }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: kat.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-xl p-4 mb-2">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3 flex-1">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 px-1 flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
              <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
              <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
            </svg>
          </div>
          {kat.medyaUrl && (
            kat.medyaTip === 'video'
              ? <video src={kat.medyaUrl} className="w-14 h-10 object-cover rounded-lg flex-shrink-0" muted />
              : <img src={kat.medyaUrl} alt="" className="w-14 h-10 object-cover rounded-lg flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <span className="font-semibold text-gray-800">{kat.ad}</span>
            {kat.medyaTip === 'video' && <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">📹 video</span>}
          </div>
        </div>
        <div className="flex gap-2 ml-2 flex-shrink-0">
          <button onClick={() => onDuzenle(kat)} className="bg-blue-100 text-blue-600 px-3 py-2 rounded-xl text-sm hover:bg-blue-200">✏️</button>
          <button onClick={() => onSil(kat)} className="bg-red-100 text-red-500 px-3 py-2 rounded-xl text-sm hover:bg-red-200">🗑️</button>
        </div>
      </div>
      {altKatlar.length > 0 && (
        <div className="mt-2 ml-8 flex flex-wrap gap-2">
          {altKatlar.map(ak => (
            <div key={ak.id} className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1">
              <span className="text-sm text-gray-600">{ak.ad}</span>
              <button onClick={() => onAltKatSil(ak.id)} className="text-red-400 hover:text-red-600 ml-1 text-xs">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Sürüklenebilir ÜRÜN kartı
// ─────────────────────────────────────────────
function SortableUrunKart({ urun, onDuzenle, onSil, onStokToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: urun.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 999 : 'auto' };
  const stokta = urun.stokta !== false;
  return (
    <div ref={setNodeRef} style={style} className={`bg-white rounded-xl p-4 mb-2 flex justify-between items-center ${!stokta ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3 flex-1">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 px-1 flex-shrink-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
            <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
            <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
          </svg>
        </div>
        {urun.resim && <img src={urun.resim} alt="" className="w-12 h-12 object-cover rounded-lg flex-shrink-0" style={{ filter: !stokta ? 'grayscale(1)' : 'none' }} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-800">{urun.ad}</h3>
            {!stokta && <span className="text-xs bg-red-100 text-red-500 px-2 py-0.5 rounded-full font-medium">Tükendi</span>}
          </div>
          {urun.altKategori && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{urun.altKategori}</span>}
          {urun.kalori && <p className="text-xs text-purple-500">🔥 {urun.kalori} kcal</p>}
          <p className="text-orange-500 font-bold">₺{urun.fiyat}</p>
        </div>
      </div>
      <div className="flex gap-2 ml-2 items-center">
        <button onClick={() => onStokToggle(urun)}
          className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${stokta ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-red-100 text-red-500 hover:bg-red-200'}`}>
          {stokta ? '✅ Var' : '❌ Yok'}
        </button>
        <button onClick={() => onDuzenle(urun)} className="bg-blue-100 text-blue-600 px-3 py-2 rounded-xl text-sm hover:bg-blue-200">✏️</button>
        <button onClick={() => onSil(urun.id)} className="bg-red-100 text-red-500 px-3 py-2 rounded-xl text-sm hover:bg-red-200">🗑️</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Medya upload yardımcı komponenti
// ─────────────────────────────────────────────
function MedyaUpload({ medyaUrl, medyaTip, yukleniyor, onChange, inputId = 'medya-input' }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 mb-2 block">📸 Resim / Video</label>
      <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-orange-400 transition-colors">
        <input type="file" accept="image/*,video/*" id={inputId} onChange={onChange} className="hidden" />
        <label htmlFor={inputId} className="cursor-pointer block">
          {yukleniyor ? (
            <p className="text-orange-500 text-sm">⏳ Yükleniyor...</p>
          ) : medyaUrl ? (
            <div>
              {medyaTip === 'video'
                ? <video src={medyaUrl} className="w-full h-24 object-cover rounded-lg mb-2" muted />
                : <img src={medyaUrl} className="w-full h-24 object-cover rounded-lg mb-2" alt="" />}
              <p className="text-xs text-green-600">✅ Yüklendi — değiştirmek için tıkla</p>
            </div>
          ) : (
            <div>
              <p className="text-gray-400 text-sm">📁 Dosya seç</p>
              <p className="text-gray-300 text-xs mt-1">JPG, PNG → WebP &nbsp;•&nbsp; MP4, MOV</p>
            </div>
          )}
        </label>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// RAPORLAR sekmesi
// ─────────────────────────────────────────────
function RaporlarSekmesi({ cafeId }) {
  const bugun = new Date().toISOString().split('T')[0];
  const [baslangic, setBaslangic] = useState(bugun);
  const [bitis, setBitis] = useState(bugun);
  const [rapor, setRapor] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [aktifTab, setAktifTab] = useState('siparis'); // siparis | urun | masa
  const [duzenleModal, setDuzenleModal] = useState(null);
  const [duzenleUrunler, setDuzenleUrunler] = useState([]);

  const hizliTarih = (tip) => {
    const bugunDate = new Date();
    if (tip === 'bugun') {
      setBaslangic(bugun); setBitis(bugun);
    } else if (tip === 'dun') {
      const dun = new Date(bugunDate); dun.setDate(dun.getDate() - 1);
      const dunStr = dun.toISOString().split('T')[0];
      setBaslangic(dunStr); setBitis(dunStr);
    } else if (tip === 'hafta') {
      const haftaBasi = new Date(bugunDate); haftaBasi.setDate(haftaBasi.getDate() - 6);
      setBaslangic(haftaBasi.toISOString().split('T')[0]); setBitis(bugun);
    } else if (tip === 'ay') {
      const ayBasi = new Date(bugunDate.getFullYear(), bugunDate.getMonth(), 1);
      setBaslangic(ayBasi.toISOString().split('T')[0]); setBitis(bugun);
    }
  };

  const raporGetir = async () => {
    setYukleniyor(true);
    try {
      const res = await fetch(`${BACKEND}/siparisler/rapor-detay/${cafeId}?baslangic=${baslangic}&bitis=${bitis}`).then(r => r.json());
      setRapor(res);
    } catch { alert('Rapor alınamadı!'); }
    setYukleniyor(false);
  };

  useEffect(() => { raporGetir(); }, []);

  const excelIndir = () => {
    if (!rapor) return;

    const wb = XLSX.utils.book_new();

    // Sayfa 1: Sipariş detayları
    const siparisData = [
      ['Tarih', 'Saat', 'Masa', 'Ürünler', 'Toplam (₺)'],
      ...rapor.satirlar.map(s => [
        s.tarih, s.saat, `Masa ${s.masaNo}`,
        s.urunler.map(u => `${u.ad} x${u.adet}`).join(', '),
        s.toplam,
      ]),
      [],
      ['', '', '', 'GENEL TOPLAM', rapor.genelToplam],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(siparisData);
    ws1['!cols'] = [{ wch: 12 }, { wch: 8 }, { wch: 8 }, { wch: 50 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Siparişler');

    // Sayfa 2: Ürün bazlı
    const urunData = [
      ['Ürün Adı', 'Satış Adedi', 'Toplam Ciro (₺)'],
      ...rapor.urunListesi.map(u => [u.ad, u.adet, u.toplam]),
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(urunData);
    ws2['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Ürün Bazlı');

    // Sayfa 3: Masa bazlı
    const masaData = [
      ['Masa No', 'Sipariş Sayısı', 'Toplam Ciro (₺)'],
      ...rapor.masaListesi.map(m => [`Masa ${m.masaNo}`, m.siparisSayisi, m.toplam]),
    ];
    const ws3 = XLSX.utils.aoa_to_sheet(masaData);
    ws3['!cols'] = [{ wch: 10 }, { wch: 14 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, ws3, 'Masa Bazlı');

    XLSX.writeFile(wb, `3amenu-rapor-${baslangic}-${bitis}.xlsx`);
  };

  const siparisiSil = async (id) => {
    if (!window.confirm('Bu siparişi silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${BACKEND}/siparisler/${id}`, { method: 'DELETE' });
      raporGetir();
    } catch { alert('Hata!'); }
  };

  const duzenleAc = (satir) => {
    setDuzenleUrunler(satir.urunler.map(u => ({ ...u })));
    setDuzenleModal(satir);
  };

  const duzenleKaydet = async () => {
    try {
      await fetch(`${BACKEND}/siparisler/${duzenleModal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urunler: duzenleUrunler }),
      });
      setDuzenleModal(null);
      raporGetir();
    } catch { alert('Hata!'); }
  };

  return (
    <div>
      {/* Filtre */}
      <div className="bg-white rounded-xl p-4 mb-4">
        <div className="flex gap-2 mb-3 flex-wrap">
          {[
            { key: 'bugun', label: 'Bugün' },
            { key: 'dun', label: 'Dün' },
            { key: 'hafta', label: 'Son 7 Gün' },
            { key: 'ay', label: 'Bu Ay' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => hizliTarih(key)}
              className="px-3 py-1.5 rounded-xl text-sm font-medium bg-orange-50 text-orange-600 hover:bg-orange-100">
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">Başlangıç</label>
            <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">Bitiş</label>
            <input type="date" value={bitis} onChange={e => setBitis(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
          </div>
          <button onClick={raporGetir} disabled={yukleniyor}
            className="bg-orange-500 text-white px-4 py-2 rounded-xl font-medium hover:bg-orange-600 disabled:opacity-50">
            {yukleniyor ? '⏳' : '🔍 Getir'}
          </button>
        </div>
      </div>

      {rapor && (
        <>
          {/* Özet kartlar */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-orange-500">₺{rapor.genelToplam.toLocaleString('tr-TR')}</p>
              <p className="text-sm text-gray-500 mt-1">Toplam Ciro</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{rapor.toplamSiparis}</p>
              <p className="text-sm text-gray-500 mt-1">Sipariş Sayısı</p>
            </div>
          </div>

          {/* Excel indir */}
          <button onClick={excelIndir}
            className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold hover:bg-green-600 mb-4 flex items-center justify-center gap-2">
            📥 Excel İndir (.xlsx)
          </button>

          {/* Tab seçici */}
          <div className="flex gap-2 mb-3">
            {[
              { key: 'siparis', label: '📋 Siparişler' },
              { key: 'urun', label: '🍽️ Ürün Bazlı' },
              { key: 'masa', label: '🪑 Masa Bazlı' },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setAktifTab(key)}
                className={`flex-1 py-2 rounded-xl text-sm font-medium ${aktifTab === key ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Sipariş tablosu */}
          {aktifTab === 'siparis' && (
            <div className="bg-white rounded-xl overflow-hidden">
              {rapor.satirlar.length === 0 ? (
                <div className="p-8 text-center text-gray-400">Bu tarih aralığında sipariş yok</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left px-4 py-3 text-gray-600 font-semibold">Tarih/Saat</th>
                        <th className="text-left px-4 py-3 text-gray-600 font-semibold">Masa</th>
                        <th className="text-left px-4 py-3 text-gray-600 font-semibold">Ürünler</th>
                        <th className="text-right px-4 py-3 text-gray-600 font-semibold">Toplam</th>
                        <th className="text-center px-4 py-3 text-gray-600 font-semibold">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rapor.satirlar.map((satir, i) => (
                        <tr key={satir.id} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            <div>{satir.tarih}</div>
                            <div className="text-xs text-gray-400">{satir.saat}</div>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800">Masa {satir.masaNo}</td>
                          <td className="px-4 py-3 text-gray-600">
                            <div className="flex flex-wrap gap-1">
                              {satir.urunler.map((u, j) => (
                                <span key={j} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-lg text-xs">
                                  {u.ad} x{u.adet}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-orange-500 whitespace-nowrap">
                            ₺{satir.toplam}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 justify-center">
                              <button onClick={() => duzenleAc(satir)}
                                className="bg-blue-100 text-blue-600 px-2 py-1 rounded-lg text-xs hover:bg-blue-200">✏️</button>
                              <button onClick={() => siparisiSil(satir.id)}
                                className="bg-red-100 text-red-500 px-2 py-1 rounded-lg text-xs hover:bg-red-200">🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-orange-50 border-t-2 border-orange-100">
                      <tr>
                        <td colSpan={3} className="px-4 py-3 font-bold text-gray-800">TOPLAM</td>
                        <td className="px-4 py-3 text-right font-bold text-orange-600 text-base">
                          ₺{rapor.genelToplam.toLocaleString('tr-TR')}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Ürün bazlı tablo */}
          {aktifTab === 'urun' && (
            <div className="bg-white rounded-xl overflow-hidden">
              {rapor.urunListesi.length === 0 ? (
                <div className="p-8 text-center text-gray-400">Veri yok</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Ürün</th>
                      <th className="text-right px-4 py-3 text-gray-600 font-semibold">Adet</th>
                      <th className="text-right px-4 py-3 text-gray-600 font-semibold">Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rapor.urunListesi.map((u, i) => (
                      <tr key={i} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                        <td className="px-4 py-3 font-medium text-gray-800">{u.ad}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{u.adet}</td>
                        <td className="px-4 py-3 text-right font-bold text-orange-500">₺{u.toplam}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-orange-50 border-t-2 border-orange-100">
                    <tr>
                      <td className="px-4 py-3 font-bold text-gray-800">TOPLAM</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">
                        {rapor.urunListesi.reduce((a, u) => a + u.adet, 0)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-orange-600">
                        ₺{rapor.genelToplam.toLocaleString('tr-TR')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}

          {/* Masa bazlı tablo */}
          {aktifTab === 'masa' && (
            <div className="bg-white rounded-xl overflow-hidden">
              {rapor.masaListesi.length === 0 ? (
                <div className="p-8 text-center text-gray-400">Veri yok</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Masa</th>
                      <th className="text-right px-4 py-3 text-gray-600 font-semibold">Sipariş</th>
                      <th className="text-right px-4 py-3 text-gray-600 font-semibold">Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rapor.masaListesi.map((m, i) => (
                      <tr key={i} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                        <td className="px-4 py-3 font-medium text-gray-800">Masa {m.masaNo}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{m.siparisSayisi}</td>
                        <td className="px-4 py-3 text-right font-bold text-orange-500">₺{m.toplam}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-orange-50 border-t-2 border-orange-100">
                    <tr>
                      <td className="px-4 py-3 font-bold text-gray-800">TOPLAM</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">{rapor.toplamSiparis}</td>
                      <td className="px-4 py-3 text-right font-bold text-orange-600">₺{rapor.genelToplam.toLocaleString('tr-TR')}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Sipariş düzenleme modal */}
      {duzenleModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center"
          onClick={e => { if (e.target === e.currentTarget) setDuzenleModal(null); }}>
          <div className="bg-white rounded-t-3xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">✏️ Sipariş Düzenle</h2>
              <button onClick={() => setDuzenleModal(null)} className="text-gray-400 text-2xl">✕</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Masa {duzenleModal.masaNo} — {duzenleModal.tarih} {duzenleModal.saat}</p>

            {duzenleUrunler.map((u, i) => (
              <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 mb-2">
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{u.ad}</p>
                  <p className="text-sm text-gray-500">₺{u.fiyat} / adet</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => {
                    const yeni = [...duzenleUrunler];
                    if (yeni[i].adet > 1) yeni[i] = { ...yeni[i], adet: yeni[i].adet - 1 };
                    else yeni.splice(i, 1);
                    setDuzenleUrunler(yeni);
                  }} className="bg-gray-200 text-gray-700 w-8 h-8 rounded-full flex items-center justify-center font-bold hover:bg-gray-300">−</button>
                  <span className="font-bold w-6 text-center">{u.adet}</span>
                  <button onClick={() => {
                    const yeni = [...duzenleUrunler];
                    yeni[i] = { ...yeni[i], adet: yeni[i].adet + 1 };
                    setDuzenleUrunler(yeni);
                  }} className="bg-orange-100 text-orange-600 w-8 h-8 rounded-full flex items-center justify-center font-bold hover:bg-orange-200">+</button>
                </div>
                <p className="font-bold text-orange-500 w-16 text-right">₺{u.fiyat * u.adet}</p>
              </div>
            ))}

            {duzenleUrunler.length === 0 && (
              <div className="text-center text-gray-400 py-4">Tüm ürünler kaldırıldı — kaydetmek siparişi siler</div>
            )}

            <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between items-center mb-4">
              <span className="font-bold text-gray-800">Yeni Toplam</span>
              <span className="font-bold text-orange-500 text-lg">
                ₺{duzenleUrunler.reduce((a, u) => a + u.fiyat * u.adet, 0)}
              </span>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setDuzenleModal(null)}
                className="flex-1 bg-gray-100 text-gray-600 py-3 rounded-2xl font-semibold">İptal</button>
              <button onClick={duzenleKaydet}
                className="flex-1 bg-orange-500 text-white py-3 rounded-2xl font-semibold hover:bg-orange-600">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// ANA KOMPONENt
// ─────────────────────────────────────────────
function AdminPanel({ kullanici }) {
  const [kategoriler, setKategoriler] = useState([]);
  const [altKategorilerMap, setAltKategorilerMap] = useState({});
  const [urunler, setUrunler] = useState([]);
  const [aktifSekme, setAktifSekme] = useState('urunler');

  const [yeniKategori, setYeniKategori] = useState('');
  const [yeniKategoriMedyaUrl, setYeniKategoriMedyaUrl] = useState('');
  const [yeniKategoriMedyaTip, setYeniKategoriMedyaTip] = useState('resim');
  const [katMedyaYukleniyor, setKatMedyaYukleniyor] = useState(false);

  const [duzenleKategori, setDuzenleKategori] = useState(null);
  const [duzenleKatMedyaYukleniyor, setDuzenleKatMedyaYukleniyor] = useState(false);

  const [yeniAltKatAnaId, setYeniAltKatAnaId] = useState('');
  const [yeniAltKatAd, setYeniAltKatAd] = useState('');

  const [secilenKategori, setSecilenKategori] = useState('');
  const [secilenAltKategori, setSecilenAltKategori] = useState('');
  const [yeniUrun, setYeniUrun] = useState({ ad: '', aciklama: '', fiyat: '' });
  const [resimYukleniyor, setResimYukleniyor] = useState(false);
  const [resimUrl, setResimUrl] = useState('');
  const [aiYukleniyor, setAiYukleniyor] = useState(false);
  const [aiSonuc, setAiSonuc] = useState(null);
  const [duzenleUrun, setDuzenleUrun] = useState(null);
  const [duzenleResimYukleniyor, setDuzenleResimYukleniyor] = useState(false);

  const [yukleniyor, setYukleniyor] = useState(false);
  const [kafeAdi, setKafeAdi] = useState('');
  const [tagline, setTagline] = useState('');
  const [slug, setSlug] = useState('');
  const [kafeKaydedildi, setKafeKaydedildi] = useState(false);
  const [masaSayisi, setMasaSayisi] = useState(10);
  const [plan, setPlan] = useState('standart');
  const [aksiyonButonlari, setAksiyonButonlari] = useState([]);
  const [yeniButonAd, setYeniButonAd] = useState('');
  const [yeniButonEmoji, setYeniButonEmoji] = useState('');
  const [degerlendirmeler, setDegerlendirmeler] = useState({ liste: [], ortalama: 0, toplam: 0 });
  const [temaRenk, setTemaRenk] = useState('#f97316');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoYukleniyor, setLogoYukleniyor] = useState(false);
  const [sosyalMedya, setSosyalMedya] = useState({
    instagram: '', facebook: '', tiktok: '', youtube: '', videoUrl: '', googleMaps: '', whatsapp: '',
  });
  const [kampanyalar, setKampanyalar] = useState([]);
  const [yeniKampanya, setYeniKampanya] = useState({ ad: '', tip: 'yuzde', indirimOrani: 10, urunler: [], baslangicSaat: '12:00', bitisSaat: '14:00', ikinciUrunBedava: false });
  const [arkaPlanVideoUrl, setArkaPlanVideoUrl] = useState('');
  const [arkaPlanVideoYukleniyor, setArkaPlanVideoYukleniyor] = useState(false);
  const [blurYogunluk, setBlurYogunluk] = useState(8);
  const [overlayOpacity, setOverlayOpacity] = useState(0.55);
  const [entegrasyon, setEntegrasyon] = useState({ netgsmKullanici: '', netgsmSifre: '', netgsmGonderen: '', sadakatAktif: false });
  const [entegrasyonKaydedildi, setEntegrasyonKaydedildi] = useState(false);
  const [topluSmsMetni, setTopluSmsMetni] = useState('');
  const [musteriler, setMusteriler] = useState([]);

  const cafeId = kullanici.uid;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const verileriGetir = async () => {
    try {
      const [k, u] = await Promise.all([
        fetch(`${BACKEND}/menu/${cafeId}`).then(r => r.json()),
        fetch(`${BACKEND}/menu/urunler/${cafeId}`).then(r => r.json()),
      ]);
      const katListe = Array.isArray(k) ? k : [];
      katListe.sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0));
      setKategoriler(katListe);

      const urunListe = Array.isArray(u) ? u : [];
      urunListe.sort((a, b) => (a.sira || 0) - (b.sira || 0));
      setUrunler(urunListe);

      const altKatMap = {};
      await Promise.all(katListe.map(async (kat) => {
        const altKatlar = await fetch(`${BACKEND}/menu/alt-kategoriler/${kat.id}`).then(r => r.json());
        altKatMap[kat.id] = Array.isArray(altKatlar) ? altKatlar : [];
      }));
      setAltKategorilerMap(altKatMap);

      const kafeRes = await fetch(`${BACKEND}/menu/kafe-by-id/${cafeId}`).then(r => r.json());
      if (kafeRes?.slug) { setSlug(kafeRes.slug); setKafeAdi(kafeRes.ad); setKafeKaydedildi(true); }
      if (kafeRes?.tagline) setTagline(kafeRes.tagline);
      if (kafeRes?.sosyalMedya) setSosyalMedya({ instagram: '', facebook: '', tiktok: '', youtube: '', videoUrl: '', googleMaps: '', whatsapp: '', ...kafeRes.sosyalMedya });
      if (kafeRes?.plan) setPlan(kafeRes.plan);
      if (kafeRes?.aksiyonButonlari) setAksiyonButonlari(kafeRes.aksiyonButonlari);
      if (kafeRes?.temaRenk) setTemaRenk(kafeRes.temaRenk);
      if (kafeRes?.logoUrl) setLogoUrl(kafeRes.logoUrl);
      if (kafeRes?.masaSayisi) setMasaSayisi(kafeRes.masaSayisi);
      if (kafeRes?.videoUrl) setArkaPlanVideoUrl(kafeRes.videoUrl);
      if (kafeRes?.blurYogunluk != null) setBlurYogunluk(kafeRes.blurYogunluk);
      if (kafeRes?.overlayOpacity != null) setOverlayOpacity(kafeRes.overlayOpacity);

      const degRes = await fetch(`${BACKEND}/siparisler/degerlendirmeler/${cafeId}`).then(r => r.json());
      if (degRes) setDegerlendirmeler(degRes);

      try {
        const kampRes = await fetch(`${BACKEND}/kampanya/${cafeId}`).then(r => r.json());
        if (Array.isArray(kampRes)) setKampanyalar(kampRes);
      } catch {}

      const kampRes = await fetch(`${BACKEND}/kampanya/${cafeId}`).then(r => r.json());
      if (Array.isArray(kampRes)) setKampanyalar(kampRes);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { verileriGetir(); }, []);

  const handleKategoriDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const eskiIndex = kategoriler.findIndex(k => k.id === active.id);
    const yeniIndex = kategoriler.findIndex(k => k.id === over.id);
    const yeniSira = arrayMove(kategoriler, eskiIndex, yeniIndex);
    setKategoriler(yeniSira);
    try {
      await Promise.all(yeniSira.map((kat, index) =>
        fetch(`${BACKEND}/menu/kategori/${kat.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sira: index }),
        })
      ));
    } catch { console.error('Kategori sırası kaydedilemedi'); }
  };

  const handleUrunDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const eskiIndex = urunler.findIndex(u => u.id === active.id);
    const yeniIndex = urunler.findIndex(u => u.id === over.id);
    const yeniSira = arrayMove(urunler, eskiIndex, yeniIndex);
    setUrunler(yeniSira);
    try {
      await Promise.all(yeniSira.map((urun, index) =>
        fetch(`${BACKEND}/menu/urun/${urun.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sira: index }),
        })
      ));
    } catch { console.error('Ürün sırası kaydedilemedi'); }
  };

  const katMedyaDosyaYukle = async (dosya) => {
    if (!dosya) return;
    setKatMedyaYukleniyor(true);
    try {
      const tip = dosya.type.startsWith('video/') ? 'video' : 'resim';
      setYeniKategoriMedyaTip(tip);
      const url = tip === 'video' ? await videoYukle(dosya) : await dosyaYukle(dosya, 'kategoriler');
      setYeniKategoriMedyaUrl(url);
    } catch (err) { alert('Yükleme hatası: ' + err.message); }
    setKatMedyaYukleniyor(false);
  };

  const kategoriEkle = async () => {
    if (!yeniKategori.trim()) return;
    setYukleniyor(true);
    try {
      await fetch(`${BACKEND}/menu/kategori`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cafeId, ad: yeniKategori, medyaUrl: yeniKategoriMedyaUrl, medyaTip: yeniKategoriMedyaTip }),
      });
      setYeniKategori(''); setYeniKategoriMedyaUrl(''); setYeniKategoriMedyaTip('resim');
      verileriGetir();
    } catch (err) { alert('Hata: ' + err.message); }
    setYukleniyor(false);
  };

  const kategoriGuncelle = async () => {
    if (!duzenleKategori) return;
    try {
      await fetch(`${BACKEND}/menu/kategori/${duzenleKategori.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ad: duzenleKategori.ad, medyaUrl: duzenleKategori.medyaUrl, medyaTip: duzenleKategori.medyaTip }),
      });
      setDuzenleKategori(null); verileriGetir();
    } catch { alert('Hata!'); }
  };

  const duzenleKatMedyaYukle = async (dosya) => {
    if (!dosya) return;
    setDuzenleKatMedyaYukleniyor(true);
    try {
      const tip = dosya.type.startsWith('video/') ? 'video' : 'resim';
      const url = tip === 'video' ? await videoYukle(dosya) : await dosyaYukle(dosya, 'kategoriler');
      setDuzenleKategori(p => ({ ...p, medyaUrl: url, medyaTip: tip }));
    } catch (err) { alert('Yükleme hatası: ' + err.message); }
    setDuzenleKatMedyaYukleniyor(false);
  };

  const kategoriSil = async (kat) => {
    if (!window.confirm(`"${kat.ad}" silinsin mi? İçindeki tüm ürünler de silinir.`)) return;
    try {
      await fetch(`${BACKEND}/menu/kategori/${kat.id}`, { method: 'DELETE' });
      verileriGetir();
    } catch { alert('Hata!'); }
  };

  const altKategoriEkle = async () => {
    if (!yeniAltKatAd.trim() || !yeniAltKatAnaId) { alert('Ana kategori ve alt kategori adı seçin!'); return; }
    try {
      await fetch(`${BACKEND}/menu/alt-kategori`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cafeId, anaKategoriId: yeniAltKatAnaId, ad: yeniAltKatAd }),
      });
      setYeniAltKatAd(''); verileriGetir();
    } catch (err) { alert('Hata: ' + err.message); }
  };

  const handleKategoriSec = (katId) => { setSecilenKategori(katId); setSecilenAltKategori(''); };

  const resimDosyaYukle = async (dosya) => {
    if (!dosya) return;
    setResimYukleniyor(true);
    try {
      const url = await dosyaYukle(dosya, 'urunler');
      setResimUrl(url); setYeniUrun(p => ({ ...p, resim: url }));
    } catch (err) { alert('Fotoğraf yüklenemedi: ' + err.message); }
    setResimYukleniyor(false);
  };

  const duzenleResimYukle = async (dosya) => {
    if (!dosya) return;
    setDuzenleResimYukleniyor(true);
    try {
      const url = await dosyaYukle(dosya, 'urunler');
      setDuzenleUrun(p => ({ ...p, resim: url }));
    } catch (err) { alert('Fotoğraf yüklenemedi: ' + err.message); }
    setDuzenleResimYukleniyor(false);
  };

  const logoYukleFunc = async (dosya) => {
    if (!dosya) return;
    setLogoYukleniyor(true);
    try {
      const url = await dosyaYukle(dosya, 'logolar');
      setLogoUrl(url);
      await fetch(`${BACKEND}/menu/kafe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cafeId, slug, ad: kafeAdi, tagline, sosyalMedya, temaRenk, logoUrl: url, videoUrl: arkaPlanVideoUrl, blurYogunluk, overlayOpacity }),
      });
      alert('Logo yüklendi! ✅');
    } catch (err) { alert('Logo yüklenemedi: ' + err.message); }
    setLogoYukleniyor(false);
  };

  const arkaPlanVideoYukle = async (dosya) => {
    if (!dosya) return;
    setArkaPlanVideoYukleniyor(true);
    try {
      const url = await videoYukle(dosya);
      setArkaPlanVideoUrl(url);
    } catch (err) { alert('Video yüklenemedi: ' + err.message); }
    setArkaPlanVideoYukleniyor(false);
  };

  const aiIleDoldur = async () => {
    if (!yeniUrun.ad.trim()) { alert('Önce ürün adını girin!'); return; }
    setAiYukleniyor(true); setAiSonuc(null);
    try {
      const res = await fetch(`${BACKEND}/menu/ai-urun-bilgi`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urunAdi: yeniUrun.ad, aciklama: yeniUrun.aciklama }),
      });
      const data = await res.json();
      if (data.success) { setAiSonuc(data); if (data.aciklama) setYeniUrun(p => ({ ...p, aciklama: data.aciklama })); }
      else alert('AI bilgisi alınamadı.');
    } catch (err) { alert('Hata: ' + err.message); }
    setAiYukleniyor(false);
  };

  const urunEkle = async () => {
    if (!yeniUrun.ad || !yeniUrun.fiyat || !secilenKategori) { alert('Tüm alanları doldurun!'); return; }
    setYukleniyor(true);
    const altKatAd = secilenAltKategori ? (altKategorilerMap[secilenKategori] || []).find(ak => ak.id === secilenAltKategori)?.ad || '' : '';
    try {
      await fetch(`${BACKEND}/menu/urun`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cafeId, kategoriId: secilenKategori, urun: { ...yeniUrun, fiyat: Number(yeniUrun.fiyat), resim: resimUrl, altKategori: altKatAd, kalori: aiSonuc?.kalori || '', alerjenler: aiSonuc?.alerjenler || [], sira: urunler.length, stokta: true } }),
      });
      setYeniUrun({ ad: '', aciklama: '', fiyat: '' }); setResimUrl(''); setSecilenAltKategori(''); setAiSonuc(null);
      verileriGetir();
    } catch { alert('Hata!'); }
    setYukleniyor(false);
  };

  const stokToggle = async (urun) => {
    const yeniStok = urun.stokta === false ? true : false;
    try {
      await fetch(`${BACKEND}/menu/urun/${urun.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...urun, stokta: yeniStok }),
      });
      verileriGetir();
    } catch { alert('Hata!'); }
  };

  const urunSil = async (urunId) => {
    if (!window.confirm('Ürünü silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${BACKEND}/menu/urun/${urunId}`, { method: 'DELETE' });
      verileriGetir();
    } catch { alert('Hata!'); }
  };

  const urunGuncelle = async () => {
    if (!duzenleUrun) return;
    try {
      await fetch(`${BACKEND}/menu/urun/${duzenleUrun.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ad: duzenleUrun.ad, aciklama: duzenleUrun.aciklama, fiyat: Number(duzenleUrun.fiyat), resim: duzenleUrun.resim, altKategori: duzenleUrun.altKategori || '', kategoriId: duzenleUrun.kategoriId, stokta: duzenleUrun.stokta !== false, oneCikan: duzenleUrun.oneCikan === true }),
      });
      setDuzenleUrun(null); verileriGetir();
    } catch { alert('Hata!'); }
  };

  const kafeKaydet = async () => {
    if (!kafeAdi.trim() || !slug.trim()) { alert('Kafe adı ve slug boş olamaz!'); return; }
    const temizSlug = slug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    try {
      await fetch(`${BACKEND}/menu/kafe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cafeId, slug: temizSlug, ad: kafeAdi, tagline, sosyalMedya, temaRenk, logoUrl, videoUrl: arkaPlanVideoUrl, blurYogunluk, overlayOpacity, masaSayisi }),
      });
      setSlug(temizSlug); setKafeKaydedildi(true);
      alert('Kafe bilgileri kaydedildi! ✅');
    } catch { alert('Hata!'); }
  };

  const butonEkle = async () => {
    if (!yeniButonAd.trim()) return;
    if (aksiyonButonlari.length >= 6) { alert('En fazla 6 aksiyon butonu!'); return; }
    const yeniListe = [...aksiyonButonlari, { ad: yeniButonAd, emoji: yeniButonEmoji || '🔔' }];
    try {
      await fetch(`${BACKEND}/menu/kafe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cafeId, slug, ad: kafeAdi, tagline, sosyalMedya, aksiyonButonlari: yeniListe, temaRenk, logoUrl, videoUrl: arkaPlanVideoUrl, blurYogunluk, overlayOpacity }) });
      setAksiyonButonlari(yeniListe); setYeniButonAd(''); setYeniButonEmoji('');
    } catch { alert('Hata!'); }
  };

  const butonSil = async (index) => {
    const yeniListe = aksiyonButonlari.filter((_, i) => i !== index);
    try {
      await fetch(`${BACKEND}/menu/kafe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cafeId, slug, ad: kafeAdi, tagline, sosyalMedya, aksiyonButonlari: yeniListe, temaRenk, logoUrl, videoUrl: arkaPlanVideoUrl, blurYogunluk, overlayOpacity }) });
      setAksiyonButonlari(yeniListe);
    } catch { alert('Hata!'); }
  };

  const qrIndir = (masa) => {
    const canvas = document.getElementById(`qr-masa-${masa}`);
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a'); a.href = url; a.download = `masa-${masa}-qr.png`; a.click();
  };

  const kampanyaEkle = async () => {
    if (!yeniKampanya.ad.trim()) { alert('Kampanya adı boş olamaz!'); return; }
    try {
      await fetch(`${BACKEND}/kampanya`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cafeId, kampanya: yeniKampanya }),
      });
      setYeniKampanya({ ad: '', tip: 'yuzde', indirimOrani: 10, urunler: [], baslangicSaat: '12:00', bitisSaat: '14:00', ikinciUrunBedava: false });
      verileriGetir();
    } catch { alert('Hata!'); }
  };

  const kampanyaSil = async (id) => {
    if (!window.confirm('Kampanya silinsin mi?')) return;
    try {
      await fetch(`${BACKEND}/kampanya/${id}`, { method: 'DELETE' });
      verileriGetir();
    } catch { alert('Hata!'); }
  };

  const kampanyaToggle = async (kampanya) => {
    try {
      await fetch(`${BACKEND}/kampanya/${kampanya.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !kampanya.aktif }),
      });
      verileriGetir();
    } catch { alert('Hata!'); }
  };

  const cikisYap = () => signOut(auth);
  const mevcutAltKatlar = secilenKategori ? (altKategorilerMap[secilenKategori] || []) : [];
  const sekmeler = ['urunler', 'kategoriler', 'kampanyalar', 'ayarlar'];
  if (plan === 'pro') sekmeler.push('aksiyonlar');
  if (plan === 'pro') sekmeler.push('entegrasyon');
  sekmeler.push('degerlendirmeler');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          {logoUrl && <img src={logoUrl} alt="logo" className="w-8 h-8 rounded-lg object-cover" />}
          <h1 className="text-xl font-bold text-gray-800">⚙️ Admin Panel</h1>
          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${plan === 'pro' ? 'bg-purple-100 text-purple-600' : 'bg-orange-100 text-orange-600'}`}>
            {plan === 'pro' ? '🚀 Pro' : '⭐ Standart'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{kullanici.email}</span>
          <a href="/kasa" target="_blank" rel="noreferrer" className="bg-gray-800 text-white px-3 py-2 rounded-xl text-sm hover:bg-gray-900">🖥️ Kasa</a>
          <a href={`/menu/${slug}/1`} target="_blank" rel="noreferrer" className="bg-orange-100 text-orange-600 px-3 py-2 rounded-xl text-sm hover:bg-orange-200">👁️ Menüme Bak</a>
          <button onClick={cikisYap} className="bg-gray-100 text-gray-600 px-3 py-2 rounded-xl text-sm hover:bg-gray-200">Çıkış</button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Sekmeler */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {sekmeler.map(sekme => (
            <button key={sekme} onClick={() => setAktifSekme(sekme)}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${aktifSekme === sekme ? 'bg-orange-500 text-white' : 'bg-white text-gray-600'}`}>
              {sekme === 'urunler' ? '🍽️ Ürünler'
                : sekme === 'kategoriler' ? '📂 Kategoriler'
                : sekme === 'kampanyalar' ? '🏷️ Kampanyalar'
                : sekme === 'ayarlar' ? '⚙️ Ayarlar'
                : sekme === 'aksiyonlar' ? '🎛️ Aksiyon Butonları'
                : sekme === 'entegrasyon' ? '🔌 Entegrasyon'
                : '⭐ Değerlendirmeler'}
            </button>
          ))}
          {plan !== 'pro' && (
            <div className="px-4 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed">🎛️ Aksiyon 🔒 Pro</div>
          )}
        </div>

        {/* ═══════════ KATEGORİLER ═══════════ */}
        {aktifSekme === 'kategoriler' && (
          <div>
            <div className="bg-white rounded-xl p-4 mb-4">
              <h2 className="font-semibold text-gray-800 mb-3">📂 Yeni Ana Kategori</h2>
              <input value={yeniKategori} onChange={e => setYeniKategori(e.target.value)} placeholder="Kategori adı"
                className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-3 focus:outline-none focus:border-orange-400" />
              <div className="mb-3">
                <MedyaUpload medyaUrl={yeniKategoriMedyaUrl} medyaTip={yeniKategoriMedyaTip} yukleniyor={katMedyaYukleniyor}
                  onChange={e => katMedyaDosyaYukle(e.target.files[0])} inputId="kat-medya-input" />
                <input value={yeniKategoriMedyaUrl} onChange={e => setYeniKategoriMedyaUrl(e.target.value)} placeholder="Veya URL gir"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2 mt-2 text-sm focus:outline-none focus:border-orange-400" />
              </div>
              <button onClick={kategoriEkle} disabled={yukleniyor}
                className="w-full bg-orange-500 text-white px-4 py-2 rounded-xl font-medium hover:bg-orange-600 disabled:opacity-50">Ana Kategori Ekle</button>
            </div>

            <div className="bg-white rounded-xl p-4 mb-4">
              <h2 className="font-semibold text-gray-800 mb-3">📁 Yeni Alt Kategori</h2>
              <select value={yeniAltKatAnaId} onChange={e => setYeniAltKatAnaId(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400">
                <option value="">Ana kategori seç</option>
                {kategoriler.map(kat => <option key={kat.id} value={kat.id}>{kat.ad}</option>)}
              </select>
              <div className="flex gap-2">
                <input value={yeniAltKatAd} onChange={e => setYeniAltKatAd(e.target.value)} placeholder="Alt kategori adı"
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-2 focus:outline-none focus:border-orange-400" />
                <button onClick={altKategoriEkle} className="bg-orange-500 text-white px-4 py-2 rounded-xl font-medium hover:bg-orange-600">Ekle</button>
              </div>
            </div>

            {kategoriler.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-gray-400">Henüz kategori yok</div>
            ) : (
              <div>
                <p className="text-xs text-gray-400 mb-2 text-center">≡ İkonunu sürükleyerek sıralamayı değiştirebilirsiniz</p>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleKategoriDragEnd}>
                  <SortableContext items={kategoriler.map(k => k.id)} strategy={verticalListSortingStrategy}>
                    {kategoriler.map(kat => (
                      <SortableKategoriKart key={kat.id} kat={kat} altKatlar={altKategorilerMap[kat.id] || []}
                        onDuzenle={setDuzenleKategori} onSil={kategoriSil}
                        onAltKatSil={async (id) => { try { await fetch(`${BACKEND}/menu/alt-kategori/${id}`, { method: 'DELETE' }); verileriGetir(); } catch { alert('Hata!'); } }} />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ ÜRÜNLER ═══════════ */}
        {aktifSekme === 'urunler' && (
          <div>
            <div className="bg-white rounded-xl p-4 mb-4">
              <h2 className="font-semibold text-gray-800 mb-3">Yeni Ürün Ekle</h2>
              <select value={secilenKategori} onChange={e => handleKategoriSec(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400">
                <option value="">Ana kategori seç</option>
                {kategoriler.map(kat => <option key={kat.id} value={kat.id}>{kat.ad}</option>)}
              </select>
              {secilenKategori && mevcutAltKatlar.length > 0 && (
                <select value={secilenAltKategori} onChange={e => setSecilenAltKategori(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400">
                  <option value="">Alt kategori seç (opsiyonel)</option>
                  {mevcutAltKatlar.map(ak => <option key={ak.id} value={ak.id}>{ak.ad}</option>)}
                </select>
              )}
              <input value={yeniUrun.ad} onChange={e => { setYeniUrun(p => ({ ...p, ad: e.target.value })); setAiSonuc(null); }}
                placeholder="Ürün adı" className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400" />
              {plan === 'pro' && (
                <button onClick={aiIleDoldur} disabled={aiYukleniyor || !yeniUrun.ad.trim()}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-2 rounded-xl font-medium mb-2 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
                  {aiYukleniyor ? <>⏳ AI analiz ediyor...</> : <>✨ AI ile Kalori & Alerjen Al</>}
                </button>
              )}
              {aiSonuc && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-2">
                  <p className="text-xs font-semibold text-purple-700 mb-2">✨ AI Sonucu</p>
                  {aiSonuc.kalori && <p className="text-sm text-gray-700 mb-1">🔥 Kalori: <strong>{aiSonuc.kalori} kcal</strong></p>}
                  {aiSonuc.alerjenler?.length > 0 && <p className="text-sm text-gray-700">⚠️ Alerjenler: <strong>{aiSonuc.alerjenler.join(', ')}</strong></p>}
                </div>
              )}
              <input value={yeniUrun.aciklama} onChange={e => setYeniUrun(p => ({ ...p, aciklama: e.target.value }))}
                placeholder="Açıklama (opsiyonel)" className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400" />
              <input value={yeniUrun.fiyat} onChange={e => setYeniUrun(p => ({ ...p, fiyat: e.target.value }))}
                placeholder="Fiyat (₺)" type="number" className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-3 focus:outline-none focus:border-orange-400" />
              <div className="mb-3">
                <label className="text-sm font-medium text-gray-700 mb-2 block">📸 Ürün Fotoğrafı</label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-orange-400 transition-colors">
                  <input type="file" accept="image/*" id="urun-resim-input" onChange={e => resimDosyaYukle(e.target.files[0])} className="hidden" />
                  <label htmlFor="urun-resim-input" className="cursor-pointer">
                    {resimYukleniyor ? <p className="text-orange-500 text-sm">⏳ Yükleniyor... (WebP)</p>
                      : resimUrl ? <div><img src={resimUrl} alt="" className="w-full h-32 object-cover rounded-lg mb-2" /><p className="text-xs text-green-600">✅ Yüklendi</p></div>
                      : <div><p className="text-gray-400 text-sm">📁 Dosya seç</p><p className="text-gray-300 text-xs mt-1">JPG, PNG → WebP</p></div>}
                  </label>
                </div>
              </div>
              <button onClick={urunEkle} disabled={yukleniyor} className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50">Ürün Ekle</button>
            </div>

            {urunler.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-gray-400">Henüz ürün yok</div>
            ) : (
              <div>
                <p className="text-xs text-gray-400 mb-2 text-center">≡ İkonunu sürükleyerek sıralamayı değiştirebilirsiniz</p>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleUrunDragEnd}>
                  <SortableContext items={urunler.map(u => u.id)} strategy={verticalListSortingStrategy}>
                    {urunler.map(urun => (
                      <SortableUrunKart key={urun.id} urun={urun} onDuzenle={setDuzenleUrun} onSil={urunSil} onStokToggle={stokToggle} />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ KAMPANYALAR ═══════════ */}
        {aktifSekme === 'kampanyalar' && (
          <div>
            <div className="bg-white rounded-xl p-4 mb-4">
              <h2 className="font-semibold text-gray-800 mb-4">🏷️ Yeni Kampanya</h2>

              <input value={yeniKampanya.ad} onChange={e => setYeniKampanya(p => ({ ...p, ad: e.target.value }))}
                placeholder="Kampanya adı (örn: Happy Hour, Öğle İndirimi)"
                className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-3 focus:outline-none focus:border-orange-400" />

              <label className="text-sm font-medium text-gray-700 mb-2 block">Kampanya Tipi</label>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { key: 'yuzde', label: '% İndirim', emoji: '🏷️' },
                  { key: 'happy_hour', label: 'Happy Hour', emoji: '⏰' },
                  { key: 'ikinci_bedava', label: '2. Bedava', emoji: '🎁' },
                ].map(tip => (
                  <button key={tip.key} onClick={() => setYeniKampanya(p => ({ ...p, tip: tip.key }))}
                    className={`py-2 px-3 rounded-xl text-sm font-medium border-2 transition-all ${yeniKampanya.tip === tip.key ? 'border-orange-500 bg-orange-50 text-orange-600' : 'border-gray-200 text-gray-600'}`}>
                    {tip.emoji} {tip.label}
                  </button>
                ))}
              </div>

              {/* Yüzde indirim */}
              {yeniKampanya.tip === 'yuzde' && (
                <div className="mb-3">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">İndirim Oranı: <span className="text-orange-500 font-bold">%{yeniKampanya.indirimOrani}</span></label>
                  <input type="range" min="5" max="70" step="5" value={yeniKampanya.indirimOrani}
                    onChange={e => setYeniKampanya(p => ({ ...p, indirimOrani: Number(e.target.value) }))}
                    className="w-full" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1"><span>%5</span><span>%35</span><span>%70</span></div>
                </div>
              )}

              {/* Happy hour */}
              {yeniKampanya.tip === 'happy_hour' && (
                <div className="mb-3">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">İndirim Oranı: <span className="text-orange-500 font-bold">%{yeniKampanya.indirimOrani}</span></label>
                  <input type="range" min="5" max="70" step="5" value={yeniKampanya.indirimOrani}
                    onChange={e => setYeniKampanya(p => ({ ...p, indirimOrani: Number(e.target.value) }))}
                    className="w-full mb-3" />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Başlangıç Saati</label>
                      <input type="time" value={yeniKampanya.baslangicSaat}
                        onChange={e => setYeniKampanya(p => ({ ...p, baslangicSaat: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Bitiş Saati</label>
                      <input type="time" value={yeniKampanya.bitisSaat}
                        onChange={e => setYeniKampanya(p => ({ ...p, bitisSaat: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">⏰ {yeniKampanya.baslangicSaat} - {yeniKampanya.bitisSaat} saatleri arasında indirim uygulanır</p>
                </div>
              )}

              {/* İkinci ürün bedava */}
              {yeniKampanya.tip === 'ikinci_bedava' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3">
                  <p className="text-sm text-green-700 font-medium">🎁 Aynı üründen 2 alana 1 bedava</p>
                  <p className="text-xs text-green-600 mt-1">Müşteri aynı üründen 2 sipariş verdiğinde 1 tanesi %50 indirimli hesaplanır.</p>
                </div>
              )}

              {/* Hangi ürünlere uygulanacak */}
              {yeniKampanya.tip !== 'ikinci_bedava' && (
                <div className="mb-3">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Hangi ürünlere uygulanacak?</label>
                  <div className="bg-gray-50 rounded-xl p-3 max-h-40 overflow-y-auto">
                    {urunler.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center">Önce ürün ekleyin</p>
                    ) : urunler.map(urun => (
                      <label key={urun.id} className="flex items-center gap-2 py-1.5 cursor-pointer">
                        <input type="checkbox"
                          checked={yeniKampanya.urunler.includes(urun.id)}
                          onChange={e => {
                            setYeniKampanya(p => ({
                              ...p,
                              urunler: e.target.checked
                                ? [...p.urunler, urun.id]
                                : p.urunler.filter(id => id !== urun.id)
                            }));
                          }}
                          className="w-4 h-4 accent-orange-500" />
                        <span className="text-sm text-gray-700">{urun.ad} — ₺{urun.fiyat}</span>
                      </label>
                    ))}
                  </div>
                  {yeniKampanya.urunler.length === 0 && (
                    <p className="text-xs text-orange-500 mt-1">⚠️ Hiç ürün seçilmezse tüm ürünlere uygulanır</p>
                  )}
                </div>
              )}

              <button onClick={kampanyaEkle} className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600">
                🏷️ Kampanya Ekle
              </button>
            </div>

            {/* Mevcut kampanyalar */}
            <h2 className="font-semibold text-gray-800 mb-3">Mevcut Kampanyalar</h2>
            {kampanyalar.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-gray-400">Henüz kampanya yok</div>
            ) : kampanyalar.map(k => (
              <div key={k.id} className={`bg-white rounded-xl p-4 mb-3 border-2 ${k.aktif ? 'border-orange-200' : 'border-gray-100 opacity-60'}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">{k.ad}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${k.aktif ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        {k.aktif ? '✅ Aktif' : '⏸️ Pasif'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {k.tip === 'yuzde' && <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full">🏷️ %{k.indirimOrani} İndirim</span>}
                      {k.tip === 'happy_hour' && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">⏰ {k.baslangicSaat}-{k.bitisSaat} | %{k.indirimOrani}</span>}
                      {k.tip === 'ikinci_bedava' && <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">🎁 2. Ürün Bedava</span>}
                      {k.urunler?.length > 0 && <span className="text-xs text-gray-400">{k.urunler.length} ürün</span>}
                      {(!k.urunler || k.urunler.length === 0) && k.tip !== 'ikinci_bedava' && <span className="text-xs text-gray-400">Tüm ürünler</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => kampanyaToggle(k)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${k.aktif ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-600'}`}>
                      {k.aktif ? 'Durdur' : 'Başlat'}
                    </button>
                    <button onClick={() => kampanyaSil(k.id)} className="bg-red-100 text-red-500 px-3 py-1.5 rounded-lg text-xs">🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ═══════════ RAPORLAR ═══════════ */}

        {/* ═══════════ AYARLAR ═══════════ */}
        {aktifSekme === 'ayarlar' && (
          <div className="bg-white rounded-xl p-4">
            <h2 className="font-semibold text-gray-800 mb-4">Kafe Bilgileri</h2>
            <input value={kafeAdi} onChange={e => setKafeAdi(e.target.value)} placeholder="Kafe adı"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-3 focus:outline-none focus:border-orange-400" />
            <input value={tagline} onChange={e => setTagline(e.target.value)} placeholder="Slogan"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-3 focus:outline-none focus:border-orange-400" />
            <input value={slug} onChange={e => setSlug(e.target.value)} placeholder="Slug"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-3 focus:outline-none focus:border-orange-400" />

            <h3 className="font-semibold text-gray-700 mt-4 mb-3">🖼️ Kafe Logosu</h3>
            <div className="flex items-center gap-4 mb-4">
              {logoUrl ? <img src={logoUrl} alt="logo" className="w-20 h-20 rounded-2xl object-cover border border-gray-200" />
                : <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl border border-gray-200">🏪</div>}
              <div className="flex-1">
                <input type="file" accept="image/*" onChange={e => logoYukleFunc(e.target.files[0])}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2 focus:outline-none focus:border-orange-400" />
                {logoYukleniyor && <p className="text-sm text-orange-500 mt-1">Logo yükleniyor...</p>}
                <p className="text-xs text-gray-400 mt-1">PNG, JPG → WebP</p>
              </div>
            </div>

            <h3 className="font-semibold text-gray-700 mt-4 mb-3">🎨 Tema Rengi</h3>
            <div className="grid grid-cols-4 gap-3 mb-4">
              {TEMA_RENKLER.map(tema => (
                <button key={tema.renk} onClick={() => setTemaRenk(tema.renk)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all ${temaRenk === tema.renk ? 'border-gray-800 scale-105' : 'border-gray-100'}`}>
                  <div className="w-8 h-8 rounded-full" style={{ backgroundColor: tema.renk }} />
                  <span className="text-xs text-gray-600">{tema.ad}</span>
                </button>
              ))}
            </div>

            {/* Blur Video Arka Plan */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <h3 className="font-semibold text-gray-700 mb-1">🎬 Menü Arka Plan Videosu</h3>
              <p className="text-xs text-gray-400 mb-3">Menü ve splash ekranının arkasında blur efektiyle oynar.</p>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-orange-400 transition-colors mb-3">
                <input type="file" accept="video/*" id="arkaplan-video-input" onChange={e => arkaPlanVideoYukle(e.target.files[0])} className="hidden" />
                <label htmlFor="arkaplan-video-input" className="cursor-pointer block">
                  {arkaPlanVideoYukleniyor ? <p className="text-orange-500 text-sm">⏳ Video yükleniyor...</p>
                    : arkaPlanVideoUrl ? <div><video src={arkaPlanVideoUrl} className="w-full h-28 object-cover rounded-lg mb-2" muted autoPlay loop /><p className="text-xs text-green-600">✅ Video yüklendi — değiştirmek için tıkla</p></div>
                    : <div><p className="text-gray-400 text-sm">📹 Video seç</p><p className="text-gray-300 text-xs mt-1">MP4, MOV — maks 200MB</p></div>}
                </label>
              </div>
              <input value={arkaPlanVideoUrl} onChange={e => setArkaPlanVideoUrl(e.target.value)} placeholder="Veya video URL gir"
                className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-4 text-sm focus:outline-none focus:border-orange-400" />
              {arkaPlanVideoUrl && (
                <button onClick={() => setArkaPlanVideoUrl('')} className="text-xs text-red-400 hover:text-red-600 mb-4 block">✕ Videoyu kaldır</button>
              )}
              <label className="text-sm font-medium text-gray-700 mb-1 block">🌫️ Blur yoğunluğu: <span className="text-orange-500 font-bold">{blurYogunluk}px</span></label>
              <input type="range" min="0" max="20" step="1" value={blurYogunluk} onChange={e => setBlurYogunluk(Number(e.target.value))} className="w-full mb-1" />
              <div className="flex justify-between text-xs text-gray-400 mb-4"><span>0</span><span>10</span><span>20</span></div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">🌑 Overlay koyuluğu: <span className="text-orange-500 font-bold">{Math.round(overlayOpacity * 100)}%</span></label>
              <input type="range" min="0" max="0.85" step="0.05" value={overlayOpacity} onChange={e => setOverlayOpacity(Number(e.target.value))} className="w-full mb-1" />
              <div className="flex justify-between text-xs text-gray-400 mb-4"><span>0%</span><span>50%</span><span>85%</span></div>
              {arkaPlanVideoUrl && (
                <div className="relative rounded-xl overflow-hidden mb-2" style={{ height: '120px' }}>
                  <video src={arkaPlanVideoUrl} autoPlay muted loop className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center" style={{ backdropFilter: `blur(${blurYogunluk}px)`, WebkitBackdropFilter: `blur(${blurYogunluk}px)`, backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}>
                    <p className="text-white text-sm font-semibold opacity-90">Önizleme</p>
                  </div>
                </div>
              )}
            </div>

            <h3 className="font-semibold text-gray-700 mt-4 mb-3">📱 Sosyal Medya</h3>
            {[{ key: 'googleMaps', ph: 'Google Maps URL' }, { key: 'whatsapp', ph: 'WhatsApp (örn: 905321234567)' }, { key: 'instagram', ph: 'Instagram URL' }, { key: 'facebook', ph: 'Facebook URL' }, { key: 'tiktok', ph: 'TikTok URL' }, { key: 'youtube', ph: 'YouTube URL' }, { key: 'videoUrl', ph: 'Tanıtım video URL (YouTube embed)' }].map(({ key, ph }) => (
              <input key={key} value={sosyalMedya[key]} onChange={e => setSosyalMedya(p => ({ ...p, [key]: e.target.value }))} placeholder={ph}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-2 focus:outline-none focus:border-orange-400" />
            ))}
            <button onClick={kafeKaydet} className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 mb-4">Kaydet</button>

            {kafeKaydedildi && (
              <div className="bg-green-50 rounded-xl p-4 mt-4">
                <p className="text-green-700 font-medium mb-4">✅ Menü linkiniz hazır!</p>
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Masa sayısı</label>
                  <input type="number" value={masaSayisi} onChange={e => setMasaSayisi(Number(e.target.value))} min="1" max="50"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2 focus:outline-none focus:border-orange-400" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: masaSayisi }, (_, i) => i + 1).map(masa => (
                    <div key={masa} className="bg-white rounded-xl p-3 text-center border border-gray-100">
                      <p className="text-sm font-medium text-gray-700 mb-2">Masa {masa}</p>
                      <QRCodeCanvas id={`qr-masa-${masa}`} value={`${SITE_URL}/menu/${slug}/${masa}`} size={100} className="mx-auto" />
                      <p className="text-xs text-gray-400 mt-2 break-all">/menu/{slug}/{masa}</p>
                      <button onClick={() => qrIndir(masa)} className="mt-2 bg-gray-800 text-white px-3 py-1 rounded-lg text-xs hover:bg-gray-900">⬇️ İndir</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ AKSİYON BUTONLARI ═══════════ */}
        {aktifSekme === 'aksiyonlar' && plan === 'pro' && (
          <div>
            <div className="bg-white rounded-xl p-4 mb-4">
              <h2 className="font-semibold text-gray-800 mb-1">🎛️ Aksiyon Butonları</h2>
              <p className="text-sm text-gray-500 mb-4">Max 6 buton.</p>
              <div className="flex gap-2 mb-2">
                <input value={yeniButonEmoji} onChange={e => setYeniButonEmoji(e.target.value)} placeholder="Emoji" className="w-16 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none text-center" />
                <input value={yeniButonAd} onChange={e => setYeniButonAd(e.target.value)} placeholder="Buton adı" className="flex-1 border border-gray-200 rounded-xl px-4 py-2 focus:outline-none focus:border-orange-400" />
              </div>
              <div className="flex gap-2 mb-4">{['🍞', '🧻', '🧊', '🥤', '🧂', '🍴'].map(emoji => (<button key={emoji} onClick={() => setYeniButonEmoji(emoji)} className="text-xl bg-gray-100 rounded-lg w-9 h-9 hover:bg-orange-100">{emoji}</button>))}</div>
              <button onClick={butonEkle} className="w-full bg-purple-500 text-white py-2 rounded-xl font-semibold hover:bg-purple-600">+ Buton Ekle</button>
            </div>
            {aksiyonButonlari.length === 0 ? <div className="bg-white rounded-xl p-8 text-center text-gray-400">Henüz aksiyon butonu yok</div> : (
              <div className="bg-white rounded-xl p-4">
                {aksiyonButonlari.map((btn, i) => (
                  <div key={i} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl mb-2">
                    <span className="font-medium text-gray-800">{btn.emoji} {btn.ad}</span>
                    <button onClick={() => butonSil(i)} className="bg-red-100 text-red-500 px-3 py-1 rounded-lg text-sm hover:bg-red-200">Sil</button>
                  </div>
                ))}
                <p className="text-xs text-gray-400 mt-2 text-center">{aksiyonButonlari.length}/6</p>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ ENTEGRASYON ═══════════ */}
        {aktifSekme === 'entegrasyon' && plan === 'pro' && (
          <div>
            <div className="bg-white rounded-xl p-4 mb-4">
              <h2 className="font-semibold text-gray-800 mb-1">🔌 NetGSM Entegrasyonu</h2>
              <p className="text-sm text-gray-500 mb-4">SMS göndermek için NetGSM API bilgilerinizi girin.</p>

              <div className="flex items-center justify-between bg-purple-50 rounded-xl px-4 py-3 mb-4">
                <div>
                  <p className="font-medium text-gray-800 text-sm">🎁 Sadakat Sistemi</p>
                  <p className="text-xs text-gray-400 mt-0.5">Açıksa müşteriler menüde sadakat kartı görebilir</p>
                </div>
                <button onClick={() => setEntegrasyon(p => ({ ...p, sadakatAktif: !p.sadakatAktif }))}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${entegrasyon.sadakatAktif ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {entegrasyon.sadakatAktif ? '✅ Açık' : '⏸️ Kapalı'}
                </button>
              </div>

              <input value={entegrasyon.netgsmKullanici} onChange={e => setEntegrasyon(p => ({ ...p, netgsmKullanici: e.target.value }))}
                placeholder="NetGSM Kullanıcı Adı"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-2 focus:outline-none focus:border-orange-400" />
              <input value={entegrasyon.netgsmSifre} onChange={e => setEntegrasyon(p => ({ ...p, netgsmSifre: e.target.value }))}
                placeholder="NetGSM Şifre" type="password"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-2 focus:outline-none focus:border-orange-400" />
              <input value={entegrasyon.netgsmGonderen} onChange={e => setEntegrasyon(p => ({ ...p, netgsmGonderen: e.target.value }))}
                placeholder="Gönderici Adı (örn: KAFEM)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-4 focus:outline-none focus:border-orange-400" />
              <p className="text-xs text-gray-400 mb-4">⚠️ Gönderici adı NetGSM panelinden onaylı olmalı. Maks 11 karakter.</p>

              <button onClick={async () => {
                try {
                  await fetch(`${BACKEND}/menu/kafe`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cafeId, slug, ad: kafeAdi, tagline, sosyalMedya, temaRenk, logoUrl, videoUrl: arkaPlanVideoUrl, blurYogunluk, overlayOpacity, entegrasyon }),
                  });
                  setEntegrasyonKaydedildi(true);
                  setTimeout(() => setEntegrasyonKaydedildi(false), 3000);
                } catch { alert('Hata!'); }
              }} className="w-full bg-purple-500 text-white py-3 rounded-xl font-semibold hover:bg-purple-600 mb-2">
                {entegrasyonKaydedildi ? '✅ Kaydedildi!' : '💾 Kaydet'}
              </button>
            </div>

            <div className="bg-white rounded-xl p-4 mb-4">
              <h2 className="font-semibold text-gray-800 mb-1">📊 Müşteri Listesi</h2>
              <button onClick={async () => {
                const res = await fetch(`${BACKEND}/sadakat/musteriler/${cafeId}`).then(r => r.json());
                setMusteriler(Array.isArray(res) ? res : []);
              }} className="w-full bg-gray-100 text-gray-700 py-2 rounded-xl font-medium mb-3 hover:bg-gray-200">🔄 Müşterileri Yükle</button>

              {musteriler.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">Henüz kayıtlı müşteri yok</p>
              ) : musteriler.map((m, i) => (
                <div key={i} className="flex justify-between items-center bg-gray-50 rounded-xl px-4 py-3 mb-2">
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{m.telefon}</p>
                    <p className="text-xs text-gray-400">{m.toplamSiparis || 0} sipariş</p>
                  </div>
                  <span className="bg-purple-100 text-purple-600 text-sm font-bold px-3 py-1 rounded-full">{m.puan || 0}/10 ☕</span>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl p-4">
              <h2 className="font-semibold text-gray-800 mb-1">📢 Toplu SMS</h2>
              <p className="text-sm text-gray-500 mb-3">Tüm kayıtlı müşterilere SMS gönder.</p>
              <textarea value={topluSmsMetni} onChange={e => setTopluSmsMetni(e.target.value)}
                placeholder="SMS metninizi yazın... (maks 160 karakter)" rows={3} maxLength={160}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-1 focus:outline-none focus:border-orange-400 resize-none text-sm" />
              <p className="text-xs text-gray-400 mb-3">{topluSmsMetni.length}/160</p>
              <button onClick={async () => {
                if (!topluSmsMetni.trim()) { alert('Mesaj boş!'); return; }
                if (!window.confirm(`Tüm müşterilere SMS gönderilecek. Emin misiniz?`)) return;
                try {
                  const res = await fetch(`${BACKEND}/sadakat/toplu-sms`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cafeId, mesaj: topluSmsMetni }),
                  }).then(r => r.json());
                  alert(`✅ ${res.gonderilen} müşteriye SMS gönderildi!`);
                  setTopluSmsMetni('');
                } catch { alert('Hata!'); }
              }} className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600">
                📢 Toplu SMS Gönder
              </button>
            </div>
          </div>
        )}

        {/* ═══════════ DEĞERLENDİRMELER ═══════════ */}
        {aktifSekme === 'degerlendirmeler' && (
          <div>
            <div className="bg-white rounded-xl p-4 mb-4 flex gap-4">
              <div className="flex-1 text-center">
                <p className="text-3xl font-bold text-orange-500">{degerlendirmeler.ortalama}</p>
                <p className="text-sm text-gray-500">Ortalama Puan</p>
                <div className="flex justify-center mt-1">{[1,2,3,4,5].map(s => <span key={s} className={s <= Math.round(degerlendirmeler.ortalama) ? 'text-yellow-400' : 'text-gray-200'}>⭐</span>)}</div>
              </div>
              <div className="flex-1 text-center">
                <p className="text-3xl font-bold text-gray-800">{degerlendirmeler.toplam}</p>
                <p className="text-sm text-gray-500">Toplam Yorum</p>
              </div>
            </div>
            {degerlendirmeler.liste.length === 0 ? <div className="bg-white rounded-xl p-8 text-center text-gray-400">Henüz değerlendirme yok</div> : (
              degerlendirmeler.liste.map((d, i) => (
                <div key={i} className="bg-white rounded-xl p-4 mb-2">
                  <div className="flex justify-between items-center mb-1">
                    <span>{'⭐'.repeat(d.puan)}{'☆'.repeat(5 - d.puan)}</span>
                    <span className="text-xs text-gray-400">Masa {d.masaNo}</span>
                  </div>
                  {d.yorum && <p className="text-gray-600 text-sm mt-1">{d.yorum}</p>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ═══════════ KATEGORİ DÜZENLEME MODALİ ═══════════ */}
      {duzenleKategori && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center" onClick={(e) => { if (e.target === e.currentTarget) setDuzenleKategori(null); }}>
          <div className="bg-white rounded-t-3xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">✏️ Kategori Düzenle</h2>
              <button onClick={() => setDuzenleKategori(null)} className="text-gray-400 text-2xl">✕</button>
            </div>
            <input value={duzenleKategori.ad || ''} onChange={e => setDuzenleKategori(p => ({ ...p, ad: e.target.value }))} placeholder="Kategori adı"
              className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-4 focus:outline-none focus:border-orange-400" />
            <div className="mb-4">
              <MedyaUpload medyaUrl={duzenleKategori.medyaUrl} medyaTip={duzenleKategori.medyaTip} yukleniyor={duzenleKatMedyaYukleniyor}
                onChange={e => duzenleKatMedyaYukle(e.target.files[0])} inputId="duzenleme-kat-medya-input" />
              <input value={duzenleKategori.medyaUrl || ''} onChange={e => setDuzenleKategori(p => ({ ...p, medyaUrl: e.target.value }))} placeholder="Veya URL gir"
                className="w-full border border-gray-200 rounded-xl px-4 py-2 mt-2 text-sm focus:outline-none focus:border-orange-400" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDuzenleKategori(null)} className="flex-1 bg-gray-100 text-gray-600 py-3 rounded-2xl font-semibold">İptal</button>
              <button onClick={kategoriGuncelle} className="flex-1 bg-orange-500 text-white py-3 rounded-2xl font-semibold hover:bg-orange-600">Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ ÜRÜN DÜZENLEME MODALİ ═══════════ */}
      {duzenleUrun && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center" onClick={(e) => { if (e.target === e.currentTarget) setDuzenleUrun(null); }}>
          <div className="bg-white rounded-t-3xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">✏️ Ürün Düzenle</h2>
              <button onClick={() => setDuzenleUrun(null)} className="text-gray-400 text-2xl">✕</button>
            </div>
            <select value={duzenleUrun.kategoriId || ''} onChange={e => setDuzenleUrun(p => ({ ...p, kategoriId: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400">
              <option value="">Kategori seç</option>
              {kategoriler.map(kat => <option key={kat.id} value={kat.id}>{kat.ad}</option>)}
            </select>
            <input value={duzenleUrun.altKategori || ''} onChange={e => setDuzenleUrun(p => ({ ...p, altKategori: e.target.value }))} placeholder="Alt kategori (opsiyonel)"
              className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400" />
            <input value={duzenleUrun.ad || ''} onChange={e => setDuzenleUrun(p => ({ ...p, ad: e.target.value }))} placeholder="Ürün adı"
              className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400" />
            <input value={duzenleUrun.aciklama || ''} onChange={e => setDuzenleUrun(p => ({ ...p, aciklama: e.target.value }))} placeholder="Açıklama"
              className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-2 focus:outline-none focus:border-orange-400" />
            <input value={duzenleUrun.fiyat || ''} onChange={e => setDuzenleUrun(p => ({ ...p, fiyat: e.target.value }))} placeholder="Fiyat (₺)" type="number"
              className="w-full border border-gray-200 rounded-xl px-4 py-2 mb-3 focus:outline-none focus:border-orange-400" />
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">📸 Fotoğraf</label>
              {duzenleUrun.resim && <img src={duzenleUrun.resim} alt="" className="w-full h-32 object-cover rounded-xl mb-2" />}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center hover:border-orange-400 transition-colors">
                <input type="file" accept="image/*" id="duzenleme-resim-input" onChange={e => duzenleResimYukle(e.target.files[0])} className="hidden" />
                <label htmlFor="duzenleme-resim-input" className="cursor-pointer">
                  {duzenleResimYukleniyor ? <p className="text-orange-500 text-sm">⏳ Yükleniyor...</p> : <p className="text-gray-400 text-sm">📁 Fotoğrafı değiştirmek için tıkla</p>}
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 mb-3">
              <div>
                <p className="font-medium text-gray-800 text-sm">Stok Durumu</p>
                <p className="text-xs text-gray-400 mt-0.5">Kapalıysa menüde "Tükendi" görünür</p>
              </div>
              <button onClick={() => setDuzenleUrun(p => ({ ...p, stokta: p.stokta === false ? true : false }))}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${duzenleUrun.stokta !== false ? 'bg-green-500 text-white' : 'bg-red-100 text-red-500'}`}>
                {duzenleUrun.stokta !== false ? '✅ Stokta Var' : '❌ Tükendi'}
              </button>
            </div>
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 mb-4">
              <div>
                <p className="font-medium text-gray-800 text-sm">⭐ Öne Çıkan Ürün</p>
                <p className="text-xs text-gray-400 mt-0.5">Menüde kategorilerin üstünde gösterilir</p>
              </div>
              <button onClick={() => setDuzenleUrun(p => ({ ...p, oneCikan: !p.oneCikan }))}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${duzenleUrun.oneCikan ? 'bg-yellow-400 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {duzenleUrun.oneCikan ? '⭐ Aktif' : '☆ Pasif'}
              </button>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDuzenleUrun(null)} className="flex-1 bg-gray-100 text-gray-600 py-3 rounded-2xl font-semibold">İptal</button>
              <button onClick={urunGuncelle} className="flex-1 bg-orange-500 text-white py-3 rounded-2xl font-semibold hover:bg-orange-600">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;